import { ArrowRightOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Alert, Button, Drawer, Space, Table, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  DashboardWorkbenchSummary,
  FeedbackWorkbenchSummary,
} from '@/facades/dashboard';
import { dashboardFacade } from '@/facades/dashboard';

const REFRESH_INTERVAL_MS = 10_000;

type FeedbackType = 'setting' | 'reader';

type FeedbackSummaryItem = FeedbackWorkbenchSummary['items'][number];

const buildFeedbackUrl = (
  feedbackType: FeedbackType,
  bundleId: string,
  platform: string,
) => {
  const query = new URLSearchParams({
    bundle_id: bundleId,
    platform,
    is_resolved: 'false',
  });
  return `/growth-config/feedbacks/${feedbackType}?${query.toString()}`;
};

const feedbackLabels: Record<FeedbackType, string> = {
  setting: '设置页反馈',
  reader: '阅读器反馈',
};

const feedbackAlertLabels: Record<FeedbackType, string> = {
  setting: '应用反馈',
  reader: '阅读反馈',
};

const feedbackTypes: FeedbackType[] = ['setting', 'reader'];

const GlobalPendingFeedbackAlert: React.FC = () => {
  const { token } = theme.useToken();
  const [workbench, setWorkbench] = useState<DashboardWorkbenchSummary | null>(
    null,
  );
  const [drawerType, setDrawerType] = useState<FeedbackType | null>(null);
  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (document.hidden || refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const result = await dashboardFacade.getWorkbench();
      setWorkbench(result);
    } catch {
      // A transient refresh failure must not interrupt the current admin workflow.
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  const hasPendingFeedback = useMemo(
    () =>
      Boolean(
        (workbench?.setting_feedback?.pending_count || 0) +
          (workbench?.reader_feedback?.pending_count || 0),
      ),
    [workbench],
  );

  const drawerSummary = drawerType
    ? drawerType === 'setting'
      ? workbench?.setting_feedback
      : workbench?.reader_feedback
    : null;

  const columns = useMemo<ColumnsType<FeedbackSummaryItem>>(
    () => [
      {
        title: '包名',
        dataIndex: 'bundle_id',
        ellipsis: true,
      },
      {
        title: '平台',
        dataIndex: 'platform',
        width: 90,
      },
      {
        title: '待处理',
        dataIndex: 'pending_count',
        width: 90,
        render: (value: number) => (
          <Typography.Text style={{ color: token.colorError }}>
            {value}
          </Typography.Text>
        ),
      },
      {
        title: '近 7 天',
        dataIndex: 'seven_day_pending_count',
        width: 90,
        render: (value: number) => (
          <Typography.Text style={{ color: token.colorError }}>
            {value}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        key: 'action',
        width: 80,
        render: (_, record) => (
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => {
              setDrawerType(null);
              history.push(
                buildFeedbackUrl(
                  drawerType || 'setting',
                  record.bundle_id,
                  record.platform,
                ),
              );
            }}
            type="link"
          >
            查看
          </Button>
        ),
      },
    ],
    [drawerType, token.colorError],
  );

  if (!hasPendingFeedback) return null;

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 99 }}>
      <Alert
        banner
        showIcon
        type="warning"
        message={
          <Space
            size={0}
            style={{
              maxWidth: '100%',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography.Text>您负责的包有待处理反馈：</Typography.Text>
            {feedbackTypes.map((type, index) => (
              <React.Fragment key={type}>
                {index > 0 ? <Typography.Text>、</Typography.Text> : null}
                <Button
                  onClick={() => setDrawerType(type)}
                  style={{ height: 'auto', padding: 0, color: token.colorText }}
                  type="link"
                >
                  {feedbackAlertLabels[type]}:
                  <Typography.Text style={{ color: token.colorError }}>
                    {type === 'setting'
                      ? workbench?.setting_feedback?.pending_count || 0
                      : workbench?.reader_feedback?.pending_count || 0}
                  </Typography.Text>
                </Button>
              </React.Fragment>
            ))}
            <Typography.Text>&nbsp;请您尽快处理!!!</Typography.Text>
          </Space>
        }
      />

      <Drawer
        open={Boolean(drawerType)}
        onClose={() => setDrawerType(null)}
        title={drawerType ? `${feedbackLabels[drawerType]}待处理明细` : ''}
        width="min(620px, 100vw)"
      >
        <Table<FeedbackSummaryItem>
          columns={columns}
          dataSource={drawerSummary?.items || []}
          pagination={false}
          rowKey={(record) => `${record.bundle_id}:${record.platform}`}
          scroll={{ x: 560 }}
          size="small"
        />
      </Drawer>
    </div>
  );
};

export default GlobalPendingFeedbackAlert;
